"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { CheckIcon, ChevronDown, X } from "lucide-react";
import React, { ReactElement, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import NoContent from "#/components/no-content";
import { useTStream } from "#/hooks/use-t-stream";
import { cn } from "#/lib/utils";
import { Badge, badgeVariants } from "#/ui/badge";
import type { VariantProps } from "class-variance-authority";
import { Command, CommandItem } from "#/ui/command";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "#/ui/form";
import { Input } from "#/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "#/ui/popover";
import clsx from "clsx";
import * as JsSearch from "js-search";
import {
  AutoSizerProps,
  ListProps,
  AutoSizer as _AutoSizer,
  List as _List,
} from "react-virtualized";
import { twMerge } from "tailwind-merge";

const List = _List as unknown as React.FC<ListProps>;
const AutoSizer = _AutoSizer as unknown as React.FC<AutoSizerProps>;

const schema = z.object({
  array_field: z.array(z.string()).optional(),
});

export default function MultiSelect({
  options,
  defaultValues,
  disabled,
  className,
  classNameInput,
  onChange,
  placeholder,
  placeholderClassName,
  title,
  onCloseAutoFocus,
  children,
  classNameTitle,
  disableSelect,
  enableSelectAll = false,
  hideCloseButton = false,
  showDetailsInSelection = false,
  selectedBadgeVariant = "muted",
  allOption,
}: {
  defaultValues: (string | number)[];
  placeholderClassName?: string;
  options: {
    value: string | number;
    label: string;
    description?: string;
    address?: {
      address_line_1?: string;
      locality?: string;
      administrative_district_level_1?: string;
      postal_code?: string;
    };
  }[];
  title?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  classNameTitle?: string;
  disableSelect?: boolean;
  classNameInput?: string;
  onCloseAutoFocus?: () => void;
  placeholder?: string | ReactElement;
  onChange?: (data: { value: string; label: string }[]) => void;
  enableSelectAll?: boolean;
  hideCloseButton?: boolean;
  showDetailsInSelection?: boolean;
  selectedBadgeVariant?: VariantProps<typeof badgeVariants>["variant"];
  allOption?: {
    label: string;
    value: string | number;
    description?: string;
    address?: {
      address_line_1?: string;
      locality?: string;
      administrative_district_level_1?: string;
      postal_code?: string;
    };
  };
}) {
  const { t } = useTStream("common");
  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState([]);

  const optionsWithAll = useMemo(() => {
    if (!allOption) return options;
    return [allOption, ...options];
  }, [options, allOption]);

  const isAllSelected = useMemo(() => {
    if (!allOption) return false;
    return (
      defaultValues?.length === 0 ||
      (defaultValues?.length === 1 && defaultValues?.[0] === allOption.value)
    );
  }, [defaultValues, allOption]);

  const displayValues = useMemo(() => {
    if (!allOption) return defaultValues;
    if (defaultValues?.length === 0) return [allOption.value];
    return defaultValues?.filter((val) => val !== allOption.value);
  }, [defaultValues, allOption]);

  const client = React.useMemo(() => {
    const s = new JsSearch.Search("label");
    s.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
    s.addIndex("label");
    s.addIndex("description");
    if (optionsWithAll.some((option) => option.address)) {
      s.addIndex("address.address_line_1");
      s.addIndex("address.locality");
      s.addIndex("address.administrative_district_level_1");
    }
    s.addDocuments(optionsWithAll);
    return s;
  }, [optionsWithAll]);

  React.useEffect(() => {
    const searchRes = client.search(search);
    const sortedSearchRes = searchRes.sort(
      (a, b) => a.label.indexOf(search) - b.label.indexOf(search),
    );
    setResults(sortedSearchRes);
  }, [search]);

  const onCapture = React.useCallback((e) => setSearch(e.target.value), []);

  const beforeChange = useCallback(
    (next) => {
      let finalValues = next;

      if (allOption) {
        const hasAllOption = next.includes(allOption.value);
        const hasIndividualOptions = next.some(
          (val) => val !== allOption.value,
        );

        if (hasAllOption && hasIndividualOptions) {
          finalValues = next.filter((val) => val !== allOption.value);
        } else if (hasAllOption && !hasIndividualOptions) {
          finalValues = [];
        }
      }

      const payload = finalValues.map((v) => ({
        value: v,
        label: optionsWithAll?.find((o) => o?.value === v)?.label,
      }));
      onChange(payload);
    },
    // `onChange` must be a dep: callers that close over render-time state
    // (the ezCater mapping step's draft) otherwise get the FIRST render's
    // handler forever and every later change is applied to stale state.
    [optionsWithAll, allOption, onChange],
  );

  const onOpenChanged = useCallback((value) => value && setSearch(""), []);
  const onSelectAll = useCallback(() => {
    if (disabled) return;
    const allValues = options.map((option) => option.value);
    beforeChange(allValues);
  }, [options, disabled, beforeChange]);

  const onClearAll = useCallback(() => {
    beforeChange([]);
  }, []);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const getDetailsText = useCallback(
    (option) => {
      if (!showDetailsInSelection) return null;

      return option.address
        ? [
            option.address.address_line_1,
            option.address.locality,
            option.address.administrative_district_level_1,
          ]
            .filter(Boolean)
            .join(", ")
        : option.description || "";
    },
    [showDetailsInSelection],
  );

  return (
    <Form {...form}>
      <FormField
        control={form.control}
        name="array_field"
        render={() => (
          <FormItem>
            <Popover modal onOpenChange={onOpenChanged}>
              <PopoverTrigger asChild data-testid="multi-select-trigger">
                <FormControl>
                  <div
                    className={cn(
                      "relative flex items-center justify-end rounded-lg border border-border bg-card data-[state=open]:border-ring",
                      className,
                      {
                        "opacity-70": disabled,
                      },
                    )}
                  >
                    <FormLabel
                      className={cn(
                        "truncate text-muted text-[11px] absolute font-circular-std font-500 left-[0.625rem] top-[-7px] px-1 bg-card z-10",
                        classNameTitle,
                      )}
                    >
                      {title}
                    </FormLabel>
                    <div
                      className={cn(
                        "relative min-h-10 overflow-y-auto flex-row mr-auto flex flex-grow flex-wrap items-center px-3 py-1 text-sm",
                        classNameInput,
                      )}
                    >
                      {displayValues?.length > 0 || isAllSelected ? (
                        optionsWithAll &&
                        optionsWithAll
                          .filter((option) =>
                            isAllSelected &&
                            allOption &&
                            option.value === allOption.value
                              ? true
                              : displayValues.includes(option.value),
                          )
                          .map((option) => (
                            <Badge
                              key={option.value}
                              variant={selectedBadgeVariant}
                              className="m-[2px] gap-1 pr-0.5"
                            >
                              <div className="flex flex-col">
                                <span className="">{option.label}</span>
                                {showDetailsInSelection &&
                                  option.value !== allOption?.value && (
                                    <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                      {getDetailsText(option)}
                                    </span>
                                  )}
                              </div>
                              <span
                                onClick={(e) => {
                                  if (
                                    disabled ||
                                    disableSelect ||
                                    hideCloseButton
                                  )
                                    return;
                                  e.preventDefault();

                                  if (
                                    allOption &&
                                    option.value === allOption.value
                                  ) {
                                    // If clicking on "all" option, don't allow removal via X button
                                    return;
                                  }

                                  beforeChange(
                                    defaultValues.filter(
                                      (v) => v !== option.value,
                                    ),
                                  );
                                }}
                                className="flex items-center rounded-sm px-[1px] hover:bg-accent hover:text-red-500"
                              >
                                {!disabled &&
                                  !disableSelect &&
                                  !hideCloseButton &&
                                  (!allOption ||
                                    option.value !== allOption.value) && (
                                    <X size={14} />
                                  )}
                              </span>
                            </Badge>
                          ))
                      ) : (
                        <span
                          className={cn(
                            "text-muted text-sm mr-auto",
                            placeholderClassName,
                          )}
                        >
                          {placeholder || t("selectPlaceholder")}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 items-center self-stretch text-muted-foreground/60">
                      {defaultValues?.length > 0 &&
                        !hideCloseButton &&
                        !isAllSelected && (
                          <div
                            onClick={(e) => {
                              if (disabled || disableSelect) return;
                              e.preventDefault();

                              beforeChange([]);
                            }}
                            className="flex items-center self-stretch p-2 hover:text-red-500"
                          >
                            {!disabled && !disableSelect && <X size={16} />}
                          </div>
                        )}
                      <div className="flex items-center pr-2 self-stretch text-muted">
                        <ChevronDown size={16} />
                      </div>
                    </div>
                  </div>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                onCloseAutoFocus={onCloseAutoFocus}
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                hidden={disabled}
                style={{
                  width: Math.max(320, window.innerWidth * 0.25),
                }} /* Ensure minimum width */
              >
                <Command>
                  <div className="p-2">
                    <Input
                      onChange={onCapture}
                      value={search}
                      className="bg-card"
                      placeholder={t("Type here")}
                    />
                  </div>

                  {enableSelectAll && (
                    <div className="flex justify-between px-2 py-1">
                      <button
                        data-testid="multi-select-select-all"
                        className="text-sm text-primary hover:underline"
                        onClick={onSelectAll}
                        disabled={disabled || disableSelect}
                      >
                        {t("Select All")}
                      </button>
                      <button
                        data-testid="multi-select-clear-all"
                        className="text-sm text-primary hover:underline"
                        onClick={onClearAll}
                        disabled={disabled || disableSelect}
                      >
                        {t("clearAll")}
                      </button>
                    </div>
                  )}
                  {children}
                  <div className="w-full h-fit">
                    <ListComponent
                      search={search}
                      data={!!search?.length ? results : optionsWithAll}
                      beforeChange={beforeChange}
                      form={form}
                      disabled={disabled || disableSelect}
                      defaultValues={defaultValues}
                      allOption={allOption}
                      isAllSelected={isAllSelected}
                    />
                  </div>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  );
}

const ListComponent = ({
  search,
  beforeChange,
  form,
  disabled,
  data,
  defaultValues,
  allOption,
  isAllSelected,
}) => {
  const { t } = useTStream("common");
  const renderItem = ({ index, style, key }) => {
    const option = { ...data[index] };

    const isAllOption = allOption && option.value === allOption.value;
    const isSelected = isAllOption
      ? isAllSelected
      : defaultValues?.includes(option.value);

    const addressDetails = option.address
      ? [
          option.address.address_line_1,
          option.address.locality,
          option.address.administrative_district_level_1,
          option.address.postal_code,
        ]
          .filter(Boolean)
          .join(", ")
      : option.description || "";

    return (
      <CommandItem
        key={option?._id || key}
        style={{
          ...style,
          height: addressDetails && !isAllOption ? "auto" : style.height,
          minHeight: style.height,
        }}
        disabled={disabled}
        onSelect={() => {
          if (disabled) return;

          if (isAllOption) {
            if (isAllSelected) {
              return;
            } else {
              beforeChange([]);
            }
          } else {
            let newValues;
            if (isSelected) {
              newValues = defaultValues.filter((v) => v !== option.value);
            } else {
              newValues = [...defaultValues, option.value];
            }
            form.setValue("array_field", newValues);
            beforeChange(newValues);
          }
        }}
        className="flex cursor-pointer flex-col items-start"
      >
        <div className="flex flex-1 w-full items-center items-center">
          <div
            className={cn(
              "mr-2 flex h-4 w-4 items-center justify-center rounded-xl border border-primary",
              isSelected
                ? "bg-[#27C499] text-white"
                : "opacity-50 [&_svg]:invisible",
            )}
          >
            <CheckIcon className={cn("h-4 w-4")} />
          </div>
          <div className="flex flex-col w-full overflow-hidden">
            <span className="font-medium line-clamp-1">{option.label}</span>
            {addressDetails && !isAllOption && (
              <span className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                {addressDetails}
              </span>
            )}
          </div>
        </div>
      </CommandItem>
    );
  };

  const containsDescription = useMemo(() => {
    return data?.some(
      (d) =>
        (!!d?.description || !!d?.address) &&
        (!allOption || d.value !== allOption.value),
    );
  }, [data, allOption]);

  return (
    <>
      <>
        <div
          className={twMerge(
            "w-full",
            clsx({
              "h-[17rem]": search?.length > 100,
              "h-[10rem]": search?.length < 100,
            }),
          )}
        >
          {!!search?.length && !data?.length ? (
            <NoContent title={t("noResultsFor", { search })} />
          ) : (
            <AutoSizer>
              {({ height, width }: any) => (
                <List
                  height={height}
                  rowCount={data.length}
                  rowHeight={containsDescription ? 60 : 35}
                  rowRenderer={renderItem}
                  width={width}
                />
              )}
            </AutoSizer>
          )}
        </div>
      </>
    </>
  );
};
